import { getCurrentSession } from './auth';
import { applyTenantRequestHeaders } from './tenantRequestContext';

export type WhiteLabelStatus = 'draft' | 'active' | 'suspended';
export type WhiteLabelDomainStatus = 'unverified' | 'pending' | 'verified' | 'failed';
// Base domains for tenant websites. Centralized so the tenant website address is
// consistent across the admin UI, branding resolution, and helpers.
//
// WHITE_LABEL_BASE_DOMAIN is the canonical hosted base domain: tenant URLs are
// computed dynamically as https://{slug}.{WHITE_LABEL_BASE_DOMAIN} — never stored.
// WHITE_LABEL_LEGACY_BASE_DOMAIN remains a recognized wildcard alias so older
// tenant links keep resolving (they must never show the marketing site).
export const WHITE_LABEL_BASE_DOMAIN = 'trade-proof.com';
export const WHITE_LABEL_LEGACY_BASE_DOMAIN = 'tradeproof.app';
export const WHITE_LABEL_LOCAL_BASE_HOST = 'localhost';

// Local dev port, following the project's env conventions (VITE_PORT), default 5173.
export const WHITE_LABEL_LOCAL_PORT = Number(
  typeof import.meta !== 'undefined' ? (import.meta.env?.VITE_PORT || 5173) : 5173,
) || 5173;

export const WHITE_LABEL_DOMAIN_STATUS_LABELS: Record<WhiteLabelDomainStatus, string> = {
  unverified: 'Not Configured',
  pending: 'Pending Verification',
  verified: 'Verified',
  failed: 'Failed',
};

export function isLocalDevelopmentBuild(): boolean {
  try {
    return typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

export function normalizeTenantSlugForDomain(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function buildLocalTenantHost(tenantSlug: string): string {
  const slug = normalizeTenantSlugForDomain(tenantSlug);
  return slug ? `${slug}.${WHITE_LABEL_LOCAL_BASE_HOST}` : '';
}

export function buildLocalTenantWebsiteUrl(tenantSlug: string): string {
  const host = buildLocalTenantHost(tenantSlug);
  return host ? `http://${host}:${WHITE_LABEL_LOCAL_PORT}` : '';
}

export function buildGeneratedTenantDomain(tenantSlug: string): string {
  const slug = normalizeTenantSlugForDomain(tenantSlug);
  return slug ? `${slug}.${WHITE_LABEL_BASE_DOMAIN}` : '';
}

export function buildProductionTenantWebsiteUrl(tenantSlug: string): string {
  const domain = buildGeneratedTenantDomain(tenantSlug);
  return domain ? `https://${domain}` : '';
}

/** Future production generated address (kept for API compatibility). */
export const buildGeneratedTenantWebsiteUrl = buildProductionTenantWebsiteUrl;

export function buildCustomDomainWebsiteUrl(customDomain: string): string {
  const host = String(customDomain || '').trim().toLowerCase();
  return host ? `https://${host}` : '';
}

// Infrastructure names that must never be used as tenant slugs on the shared
// wildcard domain. Must stay in sync with server/src/config/whiteLabelDomainConfig.js.
export const RESERVED_TENANT_SLUGS: ReadonlySet<string> = new Set([
  'www',
  'app',
  'api',
  'admin',
  'assets',
  'auth',
  'billing',
  'dashboard',
  'help',
  'login',
  'mail',
  'status',
  'support',
]);

export function isReservedTenantSlug(value: string): boolean {
  return RESERVED_TENANT_SLUGS.has(String(value || '').trim().toLowerCase());
}

/**
 * Returns true when a hostname belongs to the tenant wildcard namespaces
 * (dev {slug}.localhost / {slug}.local, canonical production {slug}.trade-proof.com,
 * and the legacy production alias {slug}.tradeproof.app). Base hosts and their
 * www/app aliases are not tenant wildcards.
 */
export function isTenantWildcardHostname(hostname: string): boolean {
  const normalized = String(hostname || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === `www.${WHITE_LABEL_BASE_DOMAIN}` || normalized === `app.${WHITE_LABEL_BASE_DOMAIN}`) return false;
  if (normalized === `www.${WHITE_LABEL_LEGACY_BASE_DOMAIN}`) return false;
  if (normalized === WHITE_LABEL_BASE_DOMAIN || normalized === WHITE_LABEL_LEGACY_BASE_DOMAIN) return true;
  if (normalized.endsWith(`.${WHITE_LABEL_BASE_DOMAIN}`)) return true;
  if (normalized.endsWith(`.${WHITE_LABEL_LEGACY_BASE_DOMAIN}`)) return true;
  return normalized.endsWith('.localhost') || normalized.endsWith('.local');
}

/**
 * Normalized lowercase hostname (protocol, IPv6 brackets, and port removed).
 */
export function normalizeTenantHostname(value: string): string {
  let next = String(value || '').trim().toLowerCase();
  try {
    if (next.includes('://')) next = new URL(next).hostname.trim().toLowerCase();
  } catch {
    // Fall back to string normalization below.
  }
  return next
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .replace(/:\d+$/, '')
    .trim()
    .toLowerCase();
}

/**
 * Base Trade-Proof hosts that may legitimately render the public marketing site:
 *   trade-proof.com / www.trade-proof.com / app.trade-proof.com  (canonical base)
 *   tradeproof.app / www.tradeproof.app                          (legacy base)
 *   localhost / 127.0.0.1
 *   preview deployments (.vercel.app / .onrender.com) and base dev aliases.
 * Every other hostname is treated as a tenant candidate (wildcard tenant or
 * verified custom domain) and must never silently fall back to the marketing page.
 */
export function isBaseTradeProofHostname(hostname: string): boolean {
  const normalized = normalizeTenantHostname(hostname);
  if (!normalized) return true;
  if (normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1' || normalized === '[::1]') return true;
  if (normalized === WHITE_LABEL_BASE_DOMAIN || normalized === `www.${WHITE_LABEL_BASE_DOMAIN}` || normalized === `app.${WHITE_LABEL_BASE_DOMAIN}`) return true;
  if (normalized === WHITE_LABEL_LEGACY_BASE_DOMAIN || normalized === `www.${WHITE_LABEL_LEGACY_BASE_DOMAIN}`) return true;
  if (normalized === 'trade-proof.localhost' || normalized === 'app.trade-proof.localhost' || normalized === 'trade-proof.local' || normalized === 'app.trade-proof.local') return true;
  if (normalized.endsWith('.vercel.app') || normalized.endsWith('.onrender.com')) return true;
  return false;
}

/**
 * Marketing-only Trade-Proof base hosts. These hostnames must ALWAYS render the
 * public marketing website and must never boot the application, run tenant
 * bootstrap, or attempt authentication:
 *   trade-proof.com / www.trade-proof.com  (canonical marketing)
 *   tradeproof.app / www.tradeproof.app    (legacy marketing alias)
 * The application host app.trade-proof.com and tenant subdomains are NOT included.
 */
export function isTradeProofMarketingOnlyHostname(hostname: string): boolean {
  const normalized = normalizeTenantHostname(hostname);
  if (!normalized) return false;
  return (
    normalized === WHITE_LABEL_BASE_DOMAIN
    || normalized === `www.${WHITE_LABEL_BASE_DOMAIN}`
    || normalized === WHITE_LABEL_LEGACY_BASE_DOMAIN
    || normalized === `www.${WHITE_LABEL_LEGACY_BASE_DOMAIN}`
  );
}

/**
 * Tenant wildcard hostnames: {slug}.trade-proof.com (canonical production),
 * {slug}.tradeproof.app (legacy production alias), and {slug}.localhost /
 * {slug}.local (local development). The bare base hosts and their www/app
 * aliases are NOT tenant wildcards.
 */
export function isWildcardTenantHostname(hostname: string): boolean {
  const normalized = normalizeTenantHostname(hostname);
  if (!normalized) return false;
  if (normalized === `www.${WHITE_LABEL_BASE_DOMAIN}` || normalized === `app.${WHITE_LABEL_BASE_DOMAIN}`) return false;
  if (normalized === `www.${WHITE_LABEL_LEGACY_BASE_DOMAIN}`) return false;
  if (normalized.endsWith(`.${WHITE_LABEL_BASE_DOMAIN}`)) return true;
  if (normalized.endsWith(`.${WHITE_LABEL_LEGACY_BASE_DOMAIN}`)) return true;
  if (normalized.endsWith('.localhost') || normalized.endsWith('.local')) return true;
  return false;
}

/**
 * Tenant login URLs used by Admin Tools "Open Website" actions. The root URL
 * redirects to /login, but pointing directly at /login removes any ambiguity for
 * admins and prospects.
 */
export function buildTenantLoginUrl(tenantSlug: string): string {
  const base = buildProductionTenantWebsiteUrl(tenantSlug);
  return base ? `${base}/login` : '';
}

export function buildCustomDomainLoginUrl(customDomain: string): string {
  const base = buildCustomDomainWebsiteUrl(customDomain);
  return base ? `${base}/login` : '';
}

export function resolveEffectiveTenantLoginUrl(config: {
  tenantSlug: string;
  customDomain: string | null;
  domainStatus: WhiteLabelDomainStatus;
}): string {
  const effective = resolveEffectiveTenantWebsite(config);
  return effective ? `${effective}/login` : '';
}

/**
 * Effective website a tenant actually uses:
 *   - verified custom domain wins when present;
 *   - otherwise the local dev URL (http://{slug}.localhost:5173) in development;
 *   - otherwise the future production URL (https://{slug}.tradeproof.app).
 */
export function resolveEffectiveTenantWebsite(config: {
  tenantSlug: string;
  customDomain: string | null;
  domainStatus: WhiteLabelDomainStatus;
}): string {
  const customDomain = String(config?.customDomain || '').trim().toLowerCase();
  if (customDomain && config?.domainStatus === 'verified') {
    return buildCustomDomainWebsiteUrl(customDomain);
  }
  if (isLocalDevelopmentBuild()) {
    return buildLocalTenantWebsiteUrl(config?.tenantSlug || '');
  }
  return buildProductionTenantWebsiteUrl(config?.tenantSlug || '');
}



export type WhiteLabelLaunchControls = {
  stocks: boolean;
  futures: boolean;
  forex: boolean;
  crypto: boolean;
  replayChart: boolean;
  navigationLayout: 'both' | 'side_only' | 'top_only';
  themeMode: 'light_only' | 'dark_only' | 'light_dark';
};

export type WhiteLabelWording = {
  companyName: string;
  productName: string;
  shortProductName: string;
  supportEmail: string;
  legalEntityName: string;
  loginHeading: string;
  loginDescription: string;
  signupHeading: string;
  signupDescription: string;
  dashboardWelcome: string;
  footerCopyright: string;
};

export type WhiteLabelConfig = {
  id: string;
  tenantSlug: string;
  priorityOrder: number;
  activeSubscribers: number;
  companyName: string;
  productName: string;
  shortProductName: string | null;
  supportEmail: string;
  legalEntityName: string | null;
  primaryLogoUrl: string | null;
  primaryLogoLightUrl: string | null;
  sidebarLogoUrl: string | null;
  sidebarLogoLightUrl: string | null;
  compactLogoUrl: string | null;
  compactLogoLightUrl: string | null;
  loadingPageLogoUrl: string | null;
  loadingPageLogoSize?: 'small' | 'medium' | 'large' | 'x-large';
  loadingPageLogoGlow?: boolean;
  useTextLogo?: boolean;
  faviconUrl: string | null;
  sidebarLogoBottomSpacing?: number;
  primaryColor: string | null;
  secondaryColor: string | null;
  partnerRevenueShare: number;
  partnerMonthlyPrice: number;
  proposalFitSummary: string | null;
  outreachFitSentence: string | null;
  customDomain: string | null;
  domainStatus: WhiteLabelDomainStatus;
  wording: Partial<WhiteLabelWording>;
  launchControls: Partial<WhiteLabelLaunchControls>;
  optionsRelevant?: boolean;
  status: WhiteLabelStatus;
  createdAt: string;
  updatedAt: string;
  activatedAt: string | null;
  publishedAt: string | null;
  publishError: string | null;
  domainRequestedAt: string | null;
  domainCheckedAt: string | null;
  domainVerifiedAt: string | null;
  domainLastError: string | null;
  vercelDomainId: string | null;
  dnsInstructions: Array<{ type: string; domain: string; value: string }> | null;
};

export type WhiteLabelCounts = {
  active: number;
  draft: number;
  suspended: number;
  total: number;
};

export type WhiteLabelBranding = {
  tenantSlug: string;
  companyName: string;
  productName: string;
  shortProductName: string;
  supportEmail: string;
  legalEntityName: string;
  primaryLogoUrl: string | null;
  primaryLogoLightUrl: string | null;
  sidebarLogoUrl: string | null;
  sidebarLogoLightUrl: string | null;
  compactLogoUrl: string | null;
  compactLogoLightUrl: string | null;
  loadingPageLogoUrl: string | null;
  loadingPageLogoSize?: 'small' | 'medium' | 'large' | 'x-large';
  loadingPageLogoGlow?: boolean;
  useTextLogo?: boolean;
  faviconUrl: string | null;
  sidebarLogoBottomSpacing?: number;
  primaryColor: string | null;
  secondaryColor: string | null;
  partnerRevenueShare: number;
  partnerMonthlyPrice: number;
  proposalFitSummary: string | null;
  outreachFitSentence: string | null;
  customDomain: string | null;
  domainStatus: WhiteLabelDomainStatus;
  wording: Partial<WhiteLabelWording>;
  launchControls: Partial<WhiteLabelLaunchControls>;
  status: WhiteLabelStatus;
  generatedUrl: string;
  effectiveUrl: string;
  publicBrandingUpdatedAt: string | null;
};

export type WhiteLabelInfrastructureStatus = 'configured' | 'not_configured' | 'unknown';

export type WhiteLabelInfrastructureState = {
  status: WhiteLabelInfrastructureStatus;
  generatedDomain: string;
  productionUrlReachable: boolean;
  checkedAt: string;
};

export type WhiteLabelPublishResult = {
  tenantId: string;
  tenantSlug: string;
  status: 'live';
  generatedUrl: string;
  effectiveUrl: string;
  publicBrandingReady: boolean;
  publishedAt: string;
};

export type PublicTenantBrandingState = 'live' | 'draft' | 'suspended' | 'not_found';

export type WhiteLabelPublicBranding = {
  state: PublicTenantBrandingState;
  tenantSlug: string;
  publicRegistrationEnabled: boolean | null;
  branding: WhiteLabelBranding | null;
  publicBrandingUpdatedAt: string | null;
};

export type WhiteLabelDeploymentList = {
  configs: WhiteLabelConfig[];
  counts: WhiteLabelCounts;
  infrastructure?: WhiteLabelInfrastructureState;
};

export type WhiteLabelConfigInput = {
  companyName: string;
  productName: string;
  shortProductName?: string;
  tenantSlug: string;
  supportEmail: string;
  legalEntityName?: string;
  primaryLogoUrl?: string;
  primaryLogoLightUrl?: string;
  sidebarLogoUrl?: string;
  sidebarLogoLightUrl?: string;
  compactLogoUrl?: string;
  compactLogoLightUrl?: string;
  loadingPageLogoUrl?: string;
  loadingPageLogoSize?: 'small' | 'medium' | 'large' | 'x-large';
  loadingPageLogoGlow?: boolean;
  useTextLogo?: boolean;
  faviconUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  partnerRevenueShare?: number;
  partnerMonthlyPrice?: number;
  proposalFitSummary?: string;
  outreachFitSentence?: string;
  customDomain?: string;
  domainStatus?: WhiteLabelDomainStatus;
  wording?: Partial<WhiteLabelWording>;
  launchControls?: Partial<WhiteLabelLaunchControls>;
  optionsRelevant?: boolean;
  addToFoundingPartnerFunnel?: boolean;
};

export const WHITE_LABEL_WORDING_TOKEN_KEYS = [
  'loginHeading',
  'loginDescription',
  'signupHeading',
  'signupDescription',
  'dashboardWelcome',
  'footerCopyright',
] as const;

const REQUEST_TIMEOUT_MS = Number(
  import.meta.env?.VITE_ADMIN_TOOLS_TIMEOUT_MS || import.meta.env?.VITE_WORKSPACE_LOAD_TIMEOUT_MS || 8000,
);
const PROPOSAL_PDF_TIMEOUT_MS = Number(import.meta.env?.VITE_PROPOSAL_PDF_TIMEOUT_MS || 30000);

type WhiteLabelRequestError = Error & { code?: string; status?: number };

function readCurrentHostname() {
  try {
    return String(window.location.hostname || '').trim();
  } catch {
    return '';
  }
}

function buildRequestError(message: string, status?: number, code?: string) {
  const error = new Error(message) as WhiteLabelRequestError;
  if (status != null) error.status = status;
  if (code) error.code = code;
  return error;
}

async function readJson<T>(res: Response): Promise<T> {
  const contentType = String(res.headers.get('content-type') || '').toLowerCase();
  const raw = await res.text();
  let body: unknown = null;
  if (contentType.includes('application/json')) {
    try { body = raw ? JSON.parse(raw) : null; } catch { body = null; }
  }

  const payload = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;

  if (!res.ok) {
    throw buildRequestError(
      String(payload?.detail || payload?.error || 'request_failed').trim() || 'request_failed',
      res.status,
      String(payload?.error || '').trim().toLowerCase() || undefined,
    );
  }

  if (!payload) {
    throw buildRequestError('White-label service returned an invalid response.', res.status, 'INVALID_RESPONSE');
  }

  return body as T;
}

function buildTimeoutError(label: string, timeoutMs: number) {
  return buildRequestError(`${label} request timed out after ${timeoutMs}ms.`);
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number, label: string): Promise<Response> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  try {
    const request = fetch(input, controller ? { ...init, signal: controller.signal } : init);
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        try {
          controller?.abort();
        } catch {}
        reject(buildTimeoutError(label, timeoutMs));
      }, timeoutMs);
    });

    return await Promise.race([request, timeout]);
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw buildTimeoutError(label, timeoutMs);
    }
    throw error;
  } finally {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
  }
}

async function buildAuthHeaders() {
  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) {
    throw buildRequestError('missing_access_token');
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  return applyTenantRequestHeaders(headers);
}

async function requestJson<T>(path: string, init?: RequestInit, label = 'White-label deployment request'): Promise<T> {
  const res = await fetchWithTimeout(
    path,
    {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(await buildAuthHeaders()),
        ...(init?.headers || {}),
      },
    },
    REQUEST_TIMEOUT_MS,
    label,
  );
  return readJson<T>(res);
}

export async function fetchWhiteLabelDeployments(): Promise<WhiteLabelDeploymentList> {
  const payload = await requestJson<{ ok: true; configs: WhiteLabelConfig[]; counts: WhiteLabelCounts; infrastructure?: WhiteLabelInfrastructureState }>(
    '/api/admin/white-label',
    undefined,
    'White-label deployments',
  );
  return {
    configs: payload.configs || [],
    counts: payload.counts || { active: 0, draft: 0, suspended: 0, total: 0 },
    infrastructure: payload.infrastructure || undefined,
  };
}

export async function fetchWhiteLabelProposalPdf(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetchWithTimeout(
    `/api/admin/white-label/${encodeURIComponent(id)}/proposal.pdf`,
    { headers: await buildAuthHeaders() },
    PROPOSAL_PDF_TIMEOUT_MS,
    'Generate proposal PDF',
  );
  if (!res.ok) {
    const payload = await res.json().catch(() => null) as { error?: string; missing?: string[] } | null;
    const error = buildRequestError(payload?.error === 'PROPOSAL_INCOMPLETE'
      ? `Proposal incomplete${payload.missing?.length ? `: ${payload.missing.join(', ')}` : '.'}`
      : 'Could not generate proposal PDF.', res.status, payload?.error);
    throw error;
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return { blob: await res.blob(), filename: match?.[1] || 'Trade-Proof_Partnership_Proposal.pdf' };
}

export async function reorderWhiteLabelDeployments(orderIds: string[]): Promise<string[]> {
  const payload = await requestJson<{ ok: true; orderIds: string[] }>(
    '/api/admin/white-label/reorder',
    { method: 'POST', body: JSON.stringify({ orderIds }) },
    'Reorder white-label deployments',
  );
  return payload.orderIds || [];
}

export async function createWhiteLabelDeployment(input: WhiteLabelConfigInput): Promise<{ config: WhiteLabelConfig; foundingPartnerWarning?: string | null }> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig; foundingPartnerWarning?: string | null }>(
    '/api/admin/white-label',
    { method: 'POST', body: JSON.stringify(input) },
    'Create white-label deployment',
  );
  return { config: payload.config, foundingPartnerWarning: payload.foundingPartnerWarning };
}

export async function updateWhiteLabelDeployment(id: string, input: Partial<WhiteLabelConfigInput>): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(input) },
    'Update white-label deployment',
  );
  return payload.config;
}

export async function activateWhiteLabelDeployment(id: string): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}/activate`,
    { method: 'POST', body: JSON.stringify({}) },
    'Activate white-label deployment',
  );
  return payload.config;
}

export async function suspendWhiteLabelDeployment(id: string): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}/suspend`,
    { method: 'POST', body: JSON.stringify({}) },
    'Suspend white-label deployment',
  );
  return payload.config;
}

export async function setWhiteLabelDeploymentStatus(id: string, status: WhiteLabelStatus): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}/status`,
    { method: 'POST', body: JSON.stringify({ status }) },
    'Update white-label status',
  );
  return payload.config;
}

export async function resetTenantOnboarding(tenantSlug: string): Promise<{ tenantSlug: string; affectedUsers: number; affectedRecords: number }> {
  const payload = await requestJson<{ ok: true; tenantSlug: string; affectedUsers: number; affectedRecords: number }>(
    '/api/admin/onboarding/tenant-reset',
    { method: 'POST', body: JSON.stringify({ tenantSlug }) },
    'Reset tenant onboarding',
  );
  return payload;
}

export async function duplicateWhiteLabelDeployment(id: string): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}/duplicate`,
    { method: 'POST', body: JSON.stringify({}) },
    'Duplicate white-label deployment',
  );
  return payload.config;
}

export async function deleteWhiteLabelDeployment(id: string): Promise<{ deleted: true; id: string; tenantSlug: string }> {
  const payload = await requestJson<{ ok: true; deleted: true; id: string; tenantSlug: string }>(
    `/api/admin/white-label/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    'Delete white-label deployment',
  );
  return payload;
}

export async function publishWhiteLabelDeployment(id: string): Promise<WhiteLabelPublishResult> {
  const payload = await requestJson<{ ok: true } & WhiteLabelPublishResult>(
    `/api/admin/white-label/${encodeURIComponent(id)}/publish`,
    { method: 'POST', body: JSON.stringify({}) },
    'Publish white-label tenant',
  );
  return payload;
}

export async function connectWhiteLabelDomain(id: string, customDomain: string): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}/domain/connect`,
    { method: 'POST', body: JSON.stringify({ customDomain }) },
    'Connect custom domain',
  );
  return payload.config;
}

export async function verifyWhiteLabelDomain(id: string): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}/domain/verify`,
    { method: 'POST', body: JSON.stringify({}) },
    'Check custom domain verification',
  );
  return payload.config;
}

export async function disconnectWhiteLabelDomain(id: string): Promise<WhiteLabelConfig> {
  const payload = await requestJson<{ ok: true; config: WhiteLabelConfig }>(
    `/api/admin/white-label/${encodeURIComponent(id)}/domain`,
    { method: 'DELETE' },
    'Disconnect custom domain',
  );
  return payload.config;
}

export async function uploadWhiteLabelLogo(options: {
  file: File;
  tenantSlug: string;
  field: 'primaryLogo' | 'primaryLogoLight' | 'sidebarLogo' | 'sidebarLogoLight' | 'compactLogo' | 'compactLogoLight' | 'loadingPageLogo' | 'favicon';
}): Promise<string> {
  const formData = new FormData();
  formData.append('file', options.file);
  formData.append('tenantSlug', options.tenantSlug);
  formData.append('field', options.field);

  const res = await fetchWithTimeout(
    '/api/admin/white-label/logo',
    {
      method: 'POST',
      headers: await buildAuthHeaders(),
      body: formData,
    },
    REQUEST_TIMEOUT_MS,
    'White-label logo upload',
  );

  const payload = await readJson<{ ok: true; url: string }>(res);
  return payload.url;
}

export async function fetchWhiteLabelBranding(): Promise<WhiteLabelBranding | null> {
  let headers: Record<string, string>;
  try {
    headers = await buildAuthHeaders();
  } catch {
    return null;
  }

  const res = await fetchWithTimeout(
    '/api/white-label/branding',
    { headers },
    REQUEST_TIMEOUT_MS,
    'White-label branding',
  );

  if (res.status === 401) return null;
  const payload = await readJson<{ ok: true; branding: WhiteLabelBranding | null }>(res);
  return payload.branding || null;
}

/**
 * Public (pre-login) tenant branding for the current hostname. Never requires
 * authentication. Includes an optional session token so an authorized platform
 * admin can preview draft branding; everyone else gets a controlled draft state.
 */
export async function fetchPublicTenantBranding(options?: { hostname?: string }): Promise<WhiteLabelPublicBranding | null> {
  const hostname = String(
    options?.hostname || readCurrentHostname(),
  ).trim().toLowerCase();

  const headers: Record<string, string> = { Accept: 'application/json' };
  applyTenantRequestHeaders(headers);
  if (hostname) {
    headers['x-forwarded-host'] = hostname;
  }
  try {
    const session = await getCurrentSession();
    const accessToken = String(session?.access_token || '').trim();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  } catch {
    // Public request — no session is required.
  }

  const query = options?.hostname ? `?hostname=${encodeURIComponent(hostname)}` : '';
  let res: Response;
  try {
    res = await fetchWithTimeout(
      `/api/tenant/public-branding${query}`,
      { headers },
      REQUEST_TIMEOUT_MS,
      'Public tenant branding',
    );
  } catch {
    return null;
  }

  if (res.status === 404) {
    return { state: 'not_found', tenantSlug: '', publicRegistrationEnabled: false, branding: null, publicBrandingUpdatedAt: null };
  }
  if (!res.ok) {
    return null;
  }

  const payload = await readJson<{
    ok: true;
    state: PublicTenantBrandingState;
    tenantSlug: string;
    publicRegistrationEnabled: boolean;
    branding: WhiteLabelBranding | null;
    publicBrandingUpdatedAt: string | null;
  }>(res);
  return {
    state: payload.state,
    tenantSlug: payload.tenantSlug || '',
    publicRegistrationEnabled: typeof payload.publicRegistrationEnabled === 'boolean'
      ? payload.publicRegistrationEnabled
      : null,
    branding: payload.branding || null,
    publicBrandingUpdatedAt: payload.publicBrandingUpdatedAt || null,
  };
}

export async function fetchPublicRegistrationState(): Promise<boolean | null> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  applyTenantRequestHeaders(headers);
  try {
    const res = await fetchWithTimeout('/api/tenant/public-registration', { headers }, REQUEST_TIMEOUT_MS, 'Public registration state');
    if (!res.ok) return false;
    const payload = await readJson<{ publicRegistrationEnabled?: boolean }>(res);
    return typeof payload.publicRegistrationEnabled === 'boolean' ? payload.publicRegistrationEnabled : null;
  } catch { return null; }
}
